import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, Repeat2, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Topbar({ onMenuClick }) {
  const { user, organization, role, logout, switchOrg } = useAuth();
  const t = useTranslation();
  const [orgs, setOrgs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/organizations")
      .then(({ data }) => { if (!cancelled) setOrgs(data); })
      .catch(() => { /* swallow; topbar is non-critical */ });
    return () => { cancelled = true; };
  }, [organization?.id]);

  const onSwitch = async (organizationId) => {
    if (organizationId === organization?.id) return;
    try {
      await switchOrg(organizationId);
      toast.success(t("common.toasts.switchedOrganization"));
    } catch (err) {
      toast.error(extractApiError(err, t("topbar.errors.couldNotSwitch")));
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      data-testid="app-topbar"
      className="fixed left-0 right-0 top-0 z-30 flex h-14 items-center border-b border-border bg-chrome/80 px-3 backdrop-blur-xl sm:px-4 md:left-64 md:px-6"
    >
      <button
        type="button"
        onClick={onMenuClick}
        className="mr-2 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border md:hidden"
        aria-label={t("topbar.openNavigation")}
        data-testid="topbar-menu-button"
      >
        <Menu className="h-4 w-4" strokeWidth={1.5} />
      </button>
      <div className="flex min-w-0 flex-col">
        <span className="mono-eyebrow">{t("topbar.organization")}</span>
        <span className="truncate font-display text-sm font-medium">{organization?.name || "-"}</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-account-button"
              className="flex h-9 min-w-0 items-center gap-2 rounded-lg border border-border px-2 text-xs transition-colors hover:border-foreground/40 sm:px-3"
            >
              <User className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
              <span className="hidden max-w-[38vw] truncate font-mono sm:inline">{user?.email}</span>
              <span className="mono-eyebrow rounded-lg border border-border px-1 py-[1px] !text-[9px]">
                {role ? t(`common.states.${role}`) : "-"}
              </span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] max-w-72">
            <DropdownMenuLabel className="mono-eyebrow !text-[9px]">{t("topbar.signedInAs")}</DropdownMenuLabel>
            <div className="break-all px-2 pb-2 font-mono text-xs">{user?.email}</div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="mono-eyebrow !text-[9px]">{t("topbar.organizations")}</DropdownMenuLabel>
            {orgs.map((o) => (
              <DropdownMenuItem
                key={o.id}
                data-testid={`topbar-org-item-${o.slug}`}
                onClick={() => onSwitch(o.id)}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-xs">{o.name}</span>
                  <span className="truncate font-mono text-[10px] text-muted-foreground">{o.slug}</span>
                </div>
                {o.id === organization?.id ? (
                  <span className="mono-eyebrow !text-[9px]">{t("common.states.current")}</span>
                ) : (
                  <Repeat2 className="h-3.5 w-3.5 opacity-60" strokeWidth={1.5} />
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="topbar-logout-button" onClick={onLogout} className="text-destructive">
              <LogOut className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> {t("topbar.logOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
